import { icon } from '../../icons';
import { t } from '../../services/i18n';
import { getEuCountryLabel, renderEuCountryOptions } from '../../services/eu-countries';
import { esc } from './onboarding-shared';
import { type ProfileDto } from '../../services/api';

export class RcOnboardingStepProfile extends HTMLElement {
  profile: ProfileDto | null = null;
  currentUserName = '';
  isLoading = false;

  render(): void {
    const allowedCountryCodes = this.profile?.allowedCountryCodes || [];
    this.innerHTML = `
      <div class="wizard-step-content">
        <h1 class="wizard-step-title">${t('onboarding.profile.title')}</h1>
        <p class="wizard-step-desc">${t('onboarding.profile.description')}</p>
        <form id="wizard-form" class="wizard-form" novalidate>
          <div class="profile-section-head">
            <h2>${t('common.name')}</h2>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="w-first-name">${t('tenantOnboarding.firstName')} <span class="required">*</span></label>
              <input id="w-first-name" name="firstName" type="text" required value="${esc(this.profile?.firstName || '')}" autocomplete="given-name">
            </div>
            <div class="form-group">
              <label for="w-last-name">${t('tenantOnboarding.lastName')} <span class="required">*</span></label>
              <input id="w-last-name" name="lastName" type="text" required value="${esc(this.profile?.lastName || '')}" autocomplete="family-name">
            </div>
          </div>
          <div class="profile-section-head">
            <h2>${t('profile.addressDetails')}</h2>
            <p>${t('profile.addressHelp')}</p>
          </div>
          <div class="form-group">
            <label for="w-account-type">${t('profile.accountType')} <span class="required">*</span></label>
            <select id="w-account-type" name="accountType" required>
              <option value="private" ${this.profile?.isBusinessAccount ? '' : 'selected'}>${t('profile.accountType.private')}</option>
              <option value="business" ${this.profile?.isBusinessAccount ? 'selected' : ''}>${t('profile.accountType.business')}</option>
            </select>
          </div>
          <div class="form-group wizard-business-only"${this.profile?.isBusinessAccount ? '' : ' hidden'}>
            <label for="w-company-name">${t('profile.companyName')} <span class="required">*</span></label>
            <input id="w-company-name" name="companyName" type="text" ${this.profile?.isBusinessAccount ? 'required' : ''} value="${esc(this.profile?.companyName || '')}" autocomplete="organization">
          </div>
          <div class="form-group wizard-business-only"${this.profile?.isBusinessAccount ? '' : ' hidden'}>
            <label for="w-vat-number">${t('profile.vatNumber')} <span class="required">*</span></label>
            <input id="w-vat-number" name="vatNumber" type="text" ${this.profile?.isBusinessAccount ? 'required' : ''} value="${esc(this.profile?.vatNumber || '')}" placeholder="BE0123456789" autocomplete="off">
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="w-street">${t('profile.street')} <span class="required">*</span></label>
              <input id="w-street" name="street" type="text" required value="${esc(this.profile?.street || '')}" autocomplete="address-line1">
            </div>
            <div class="form-group">
              <label for="w-house-number">${t('profile.houseNumber')} <span class="required">*</span></label>
              <input id="w-house-number" name="houseNumber" type="text" required value="${esc(this.profile?.houseNumber || '')}" autocomplete="address-line2">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label for="w-postal-code">${t('profile.postalCode')} <span class="required">*</span></label>
              <input id="w-postal-code" name="postalCode" type="text" required value="${esc(this.profile?.postalCode || '')}" autocomplete="postal-code">
            </div>
            <div class="form-group">
              <label for="w-city">${t('profile.city')} <span class="required">*</span></label>
              <input id="w-city" name="city" type="text" required value="${esc(this.profile?.city || '')}" autocomplete="address-level2">
            </div>
          </div>
          <div class="form-group">
            <label for="w-country">${t('profile.country')} <span class="required">*</span></label>
            <input id="w-country" name="country" type="text" list="w-country-options" required value="${esc(getEuCountryLabel(this.profile?.country, allowedCountryCodes))}" autocomplete="country-name" placeholder="${t('profile.countryPlaceholder')}">
            <datalist id="w-country-options">${renderEuCountryOptions(this.profile?.country, allowedCountryCodes)}</datalist>
          </div>
          <div class="wizard-business-only"${this.profile?.isBusinessAccount ? '' : ' hidden'}>
            <div class="profile-section-head">
              <h2>${t('profile.billingAddress')}</h2>
              <p>${t('onboarding.profile.billingHelp')}</p>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="w-billing-street">${t('profile.street')}</label>
                <input id="w-billing-street" name="billingStreet" type="text" value="${esc(this.profile?.billingStreet || '')}" autocomplete="address-line1">
              </div>
              <div class="form-group">
                <label for="w-billing-house-number">${t('profile.houseNumber')}</label>
                <input id="w-billing-house-number" name="billingHouseNumber" type="text" value="${esc(this.profile?.billingHouseNumber || '')}" autocomplete="address-line2">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="w-billing-postal-code">${t('profile.postalCode')}</label>
                <input id="w-billing-postal-code" name="billingPostalCode" type="text" value="${esc(this.profile?.billingPostalCode || '')}" autocomplete="postal-code">
              </div>
              <div class="form-group">
                <label for="w-billing-city">${t('profile.city')}</label>
                <input id="w-billing-city" name="billingCity" type="text" value="${esc(this.profile?.billingCity || '')}" autocomplete="address-level2">
              </div>
            </div>
            <div class="form-group">
              <label for="w-billing-country">${t('profile.country')}</label>
              <input id="w-billing-country" name="billingCountry" type="text" list="w-billing-country-options" value="${esc(getEuCountryLabel(this.profile?.billingCountry, allowedCountryCodes))}" autocomplete="country-name" placeholder="${t('profile.countryPlaceholder')}">
              <datalist id="w-billing-country-options">${renderEuCountryOptions(this.profile?.billingCountry, allowedCountryCodes)}</datalist>
            </div>
          </div>
          <div id="wizard-error" class="wizard-error" style="display:none"></div>
          <div class="wizard-actions">
            <div></div>
            <button type="submit" class="btn btn-primary btn-lg wizard-next-btn">
              ${t('onboarding.profile.next')}
              ${icon('arrow-right', { size: 16, strokeWidth: 2.5 })}
            </button>
          </div>
        </form>
      </div>
    `;

    this.querySelector<HTMLFormElement>('#wizard-form')?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.dispatchEvent(new CustomEvent('onboarding-submit', {
        bubbles: true,
        detail: { form: event.currentTarget as HTMLFormElement },
      }));
    });
    this.querySelector<HTMLSelectElement>('#w-account-type')?.addEventListener('change', (event) => {
      const isBusinessAccount = (event.currentTarget as HTMLSelectElement).value === 'business';
      this.querySelectorAll<HTMLElement>('.wizard-business-only').forEach((element) => {
        element.hidden = !isBusinessAccount;
      });
      this.querySelectorAll<HTMLInputElement>('#w-company-name, #w-vat-number').forEach((input) => {
        input.required = isBusinessAccount;
        input.setCustomValidity('');
      });
      this.dispatchEvent(new CustomEvent('onboarding-account-type-change', {
        bubbles: true,
        detail: { isBusinessAccount },
      }));
    });
    this.querySelectorAll<HTMLInputElement>('#w-company-name, #w-vat-number').forEach((input) => {
      input.addEventListener('input', () => {
        input.setCustomValidity('');
      });
    });
  }
}

if (!customElements.get('rc-onboarding-step-profile')) {
  customElements.define('rc-onboarding-step-profile', RcOnboardingStepProfile);
}
